<template>
  <div class="theme-switch-container">
    <button 
      @click="toggleTheme" 
      class="theme-switch"
      :class="{ 'dark-mode': isDarkMode }"
      :aria-label="isDarkMode ? $t('themeSwitch.toLight') : $t('themeSwitch.toDark')"
    >
      <div class="switch-track">
        <div class="switch-thumb">
          <div class="icon-container">
            <span class="sun-icon" v-if="!isDarkMode">☀️</span>
            <span class="moon-icon" v-else>🌙</span>
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ThemeSwitch',
  data() {
    return {
      isDarkMode: true // Default to dark mode
    }
  },
  mounted() {
    // Load saved theme preference or default to dark
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark'
    } else {
      // Default to dark mode
      this.isDarkMode = true
    }
    this.applyTheme()
  },
  methods: {
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode
      this.applyTheme()
      this.saveTheme()
    },
    applyTheme() {
      const root = document.documentElement
      if (this.isDarkMode) {
        root.classList.remove('light-theme')
        // Dark theme is default, no need to add class
      } else {
        root.classList.add('light-theme')
      }
      
      // No body overflow manipulation; keep native scrolling behavior
    },
    saveTheme() {
      localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light')
    }
  }
}
</script>

<style scoped>
.theme-switch-container {
  position: relative;
  z-index: 1000;
}

.theme-switch {
  background: rgba(15, 16, 35, 0.6);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 25px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

.theme-switch:hover {
  background: rgba(15, 16, 35, 0.8);
  border-color: rgba(0, 255, 179, 0.3);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  transform: translateY(-2px);
}

.theme-switch:active {
  transform: translateY(0);
}

.switch-track {
  width: 60px;
  height: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.theme-switch:not(.dark-mode) .switch-track {
  /* Softere, harmonische Farben im Light Theme */
  background: linear-gradient(135deg, #e0e7ff 0%, #d8b4fe 100%);
}

.switch-thumb {
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.theme-switch:not(.dark-mode) .switch-thumb {
  left: 32px;
  background: rgba(255, 255, 255, 0.95);
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 14px;
  transition: all 0.3s ease;
}

.sun-icon {
  animation: rotate 2s linear infinite;
}

.moon-icon {
  animation: float 2s ease-in-out infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-2px); }
}

/* Light theme styles */
:deep(.light-theme) {
  --bg-primary: #ffffff;
  --bg-secondary: #f8f9fa;
  --text-primary: #2c3e50;
  --text-secondary: #6c757d;
  --accent-primary: #667eea;
  --accent-secondary: #764ba2;
  --border-color: #e9ecef;
}

/* Force a white border for the switch in light theme for better visual contrast */
:deep(.light-theme) .theme-switch {
  /* Harmonischer Glass-Look im Light Theme */
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px) saturate(150%);
  -webkit-backdrop-filter: blur(14px) saturate(150%);
  border: 2px solid #ffffff;
  box-shadow: 0 6px 24px rgba(2, 6, 23, 0.08);
}

:deep(.light-theme) .theme-switch:hover {
  border: 2px solid #ffffff;
  box-shadow: 0 8px 28px rgba(2, 6, 23, 0.12);
  transform: translateY(-1px);
}

:deep(.dark-theme) {
  --bg-primary: #0f1023;
  --bg-secondary: #1a1b3a;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;
  --accent-primary: #667eea;
  --accent-secondary: #764ba2;
  --border-color: rgba(255, 255, 255, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .theme-switch {
    padding: 6px;
  }
  
  .switch-track {
    width: 50px;
    height: 25px;
  }
  
  .switch-thumb {
    width: 21px;
    height: 21px;
  }
  
  .theme-switch:not(.dark-mode) .switch-thumb {
    left: 27px;
  }
  
  .icon-container {
    font-size: 12px;
  }
}
</style>